import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useSubscriptionPermissions } from '~/hooks/useSubscriptionPermissions';
import type { CalComBooking } from '~/interfaces/cal_com';
import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';
import { useAccessFulfillment } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useAccessFulfillment';
import useOrderReceipt from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useOrderReceipt';
import useStaticOrders from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useStaticOrders';
import toast from '~/utils/toast';

import { OrdersContext, type OrdersContextType } from './OrdersContext';

type Props = {
  publication: Publication;
  subscription: Subscription;
  subscriberId: string;
  isLoggedIn: boolean;
  children: React.ReactNode;
};

/**
 * Owns the Orders-tab domain — orders data + receipt fetching, fulfillment access, product-review
 * navigation, and the Cal.com coaching-reschedule modal state. Self-calls `useStaticOrders` and
 * `useAccessFulfillment`. Renders no UI; the colocated `OrdersSection` child renders the orders
 * list, the request-access teaser, and the reschedule modal from context. Mounted locally around
 * the Orders tab in `PageInner`, so the fetchers only load when that tab is open.
 */
export const OrdersProvider = ({ publication, subscription, subscriberId, isLoggedIn, children }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { canManageOrders } = useSubscriptionPermissions(subscription, isLoggedIn);

  const {
    accessFulfillment,
    isRescheduling,
    isReschedulingModalOpen,
    reschedulingData,
    handleCloseRescheduling,
    handleRescheduleBookingSuccess,
  } = useAccessFulfillment();

  const { data: orders, refetch: refetchOrders } = useStaticOrders({
    enabled: isLoggedIn,
    subscriberId,
  });

  const onRescheduleBookingSuccess = useCallback(
    (fulfillmentId: string) => async (bookingData: CalComBooking) => {
      try {
        await handleRescheduleBookingSuccess(fulfillmentId, bookingData);
        refetchOrders();
        toast.success(t('manage.orders.order.booking.rescheduled_message'));
      } catch {
        toast.error(t('manage.orders.order.booking.rescheduled_error'));
        handleCloseRescheduling();
      }
    },
    [handleRescheduleBookingSuccess, refetchOrders, handleCloseRescheduling, t],
  );

  const { viewReceipt: onViewReceipt, pendingReceiptOrderId } = useOrderReceipt();

  const onClickReviewProduct = useCallback(
    (orderId: string, orderItemId: string, productSlug: string) => {
      navigate(
        `/subscribe/${subscriberId}/review_order?review_order_id=${orderId}&review_order_item_id=${orderItemId}&review_product_slug=${productSlug}`,
      );
    },
    [navigate, subscriberId],
  );

  const subscriberFullName = useMemo(() => {
    const firstName = subscription?.first_name;
    const lastName = subscription?.last_name;
    if (!firstName && !lastName) return '';
    if (!firstName) return '';
    if (!lastName) return firstName ?? '';
    return `${firstName} ${lastName}`;
  }, [subscription?.first_name, subscription?.last_name]);

  const value = useMemo<OrdersContextType>(
    () => ({
      orders,
      canManageOrders,
      isLoggedIn,
      pendingReceiptOrderId,
      subscriberEmail: subscription.email,
      subscriberFullName,
      publicationId: publication.id,
      onViewReceipt,
      onClickReviewProduct,
      onAccessFulfillment: accessFulfillment,
      isReschedulingModalOpen,
      isRescheduling,
      reschedulingData,
      onCloseRescheduling: handleCloseRescheduling,
      onRescheduleBookingSuccess,
    }),
    [
      orders,
      canManageOrders,
      isLoggedIn,
      pendingReceiptOrderId,
      subscription.email,
      subscriberFullName,
      publication.id,
      onViewReceipt,
      onClickReviewProduct,
      accessFulfillment,
      isReschedulingModalOpen,
      isRescheduling,
      reschedulingData,
      handleCloseRescheduling,
      onRescheduleBookingSuccess,
    ],
  );

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
};
